/**
 * Ein Fehler in einer Ansicht darf nicht den Rahmen mitnehmen.
 *
 * React bricht bei einem Fehler im Rendern den **ganzen** Baum ab. Ohne
 * Fehlergrenze heißt das: eine weiße Seite. Kein Header, keine Navigation,
 * kein Hinweis, was zu tun ist — und für den Benutzer nicht von einem Ausfall
 * des Servers zu unterscheiden.
 *
 * Nach ADR-040 zeichnet die Shell den Rahmen für alles, auch für eingebettete
 * Fremdoberflächen. Genau deshalb liegt die Grenze **innerhalb** der Shell und
 * nicht um sie herum: Was scheitert, ist eine Ansicht; Header und Navigation
 * haben damit nichts zu tun und bleiben bedienbar. Wer in einer kaputten
 * Ansicht landet, kommt mit einem Klick woandershin, statt neu laden zu
 * müssen.
 *
 * ## Was der Benutzer sieht — und was nicht
 *
 * `04` §4.6 hält fest: „An Benutzeroberflächen werden keine internen
 * Stacktraces, Tokens, Bucket-Namen oder Originalantworten weitergegeben."
 * Der Satz steht dort für Adapterfehler, und er gilt hier genauso — ein
 * abgestürztes Bauteil trägt in seiner Meldung womöglich einen Dateinamen,
 * eine Mailadresse oder einen Pfad.
 *
 * Der Benutzer bekommt deshalb **keinen** Fehlertext, sondern eine Kennung.
 * Sie ist die Brücke zwischen „bei mir geht es nicht" und dem
 * Protokolleintrag, und sie ist das einzige, was beide Seiten teilen.
 *
 * **Kein `ErrorEnvelope`.** Die zehn Codes aus `04` §4.6 beschreiben Fehler
 * eines Adapters, der über eine Schnittstelle geantwortet hat. Ein Absturz im
 * Browser ist keiner davon, und sich einen Code zu leihen hieße, im Protokoll
 * später einen Adapterfehler zu sehen, wo keiner war.
 *
 * ## Warum das Protokoll durch den Filter geht
 *
 * `console.error(fehler)` wäre der bequeme Weg und schriebe den ungefilterten
 * Stacktrace in die Browserkonsole. Das ist nicht nur eine lokale Ausgabe:
 * Fehlerberichtsdienste greifen Konsolenausgaben ab (ADR-030 sieht GlitchTip
 * vor), und dann verlässt genau das den Stack, was `packages/telemetry`
 * draußen halten soll. Der Filter läuft deshalb **vor** der Ausgabe, nicht
 * beim Versand.
 */

import { Component, type ErrorInfo, type ReactNode } from "react";

import { filtern } from "@hwc/telemetry";

/** Kennung für die Rückfrage. `randomUUID` fehlt in älteren Browsern. */
function kennung(): string {
  try {
    return crypto.randomUUID();
  } catch {
    return `ohne-uuid-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;
  }
}

export interface FehlergrenzeEigenschaften {
  readonly children: ReactNode;
  /**
   * Wechselt dieser Wert, versucht die Grenze es erneut.
   *
   * Ohne das bliebe die Fehlermeldung stehen, wenn der Benutzer zu einer
   * anderen Ansicht wechselt: Der Fehlerzustand gehört der Grenze, nicht der
   * Route, und niemand räumt ihn ab. Die Shell reicht den Pfad herein.
   */
  readonly zuruecksetzenBei?: string;
}

interface Zustand {
  readonly kennung: string | null;
}

export class Fehlergrenze extends Component<FehlergrenzeEigenschaften, Zustand> {
  override state: Zustand = { kennung: null };

  static getDerivedStateFromError(): Zustand {
    return { kennung: kennung() };
  }

  override componentDidUpdate(vorher: FehlergrenzeEigenschaften) {
    if (
      this.state.kennung !== null &&
      vorher.zuruecksetzenBei !== this.props.zuruecksetzenBei
    ) {
      this.setState({ kennung: null });
    }
  }

  override componentDidCatch(fehler: Error, info: ErrorInfo) {
    const { bericht, entfernt, maskiert } = filtern({
      fehlertyp: fehler.name,
      meldung: fehler.message,
      stacktrace: fehler.stack ?? "",
      programmpfad: info.componentStack ?? "",
      correlationId: this.state.kennung ?? "",
    });
    // Eine Zeile, die man wiederfindet: Die Kennung steht vorn, weil danach
    // gesucht wird, wenn jemand anruft.
    console.error("[hwc] Ansicht abgestürzt", this.state.kennung, {
      ...bericht,
      ...(entfernt.length ? { entfernteFelder: entfernt } : {}),
      ...(maskiert.length ? { maskiert } : {}),
    });
  }

  override render() {
    if (this.state.kennung === null) return this.props.children;
    return (
      <section className="absturz" role="alert">
        <h1>Diese Ansicht konnte nicht angezeigt werden</h1>
        <p>
          Der übrige Teil des Portals funktioniert weiter — Navigation und
          Kopfzeile sind bedienbar. Am ehesten hilft, die Seite neu zu laden.
        </p>
        <p className="absturz__kennung">
          Kennung für Rückfragen: <code>{this.state.kennung}</code>
        </p>
      </section>
    );
  }
}
